import { useState } from "react";
import { Check, ChevronRight, Pencil, Plus, Tag, Trash2, Wallet, X } from "lucide-react";

import { ApiError } from "@/lib/api";
import { useCurrentBudget } from "@/lib/budget";
import { useFinance } from "@/lib/hooks";
import { DEFAULT_TAG_COLOR, TAG_COLORS, TAG_COLOR_KEYS, tagStyle, type Budget, type ExpenseTag, type TagColor } from "@/lib/meta";
import { cn } from "@/lib/utils";
import { useNavigate } from "@/components/app-shell";
import { FinanceTabs } from "@/components/finance-tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

/** Turns a failed request into something worth showing the user. */
function message(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Something went wrong. Please try again.";
}

export function FinanceConfigPage() {
  const { tags, budgets, loading, addTag, updateTag, deleteTag, addBudget, updateBudget, deleteBudget } = useFinance();
  const [, selectBudget] = useCurrentBudget(budgets);
  const navigate = useNavigate();

  // Opening a budget makes it the current one, so the budget page and the
  // Overview both land on what was just clicked.
  const openBudget = (id: string) => {
    selectBudget(id);
    navigate("finance-budget", id);
  };

  return (
    <div className="space-y-5">
      <FinanceTabs active="finance-config" />

      <BudgetsCard
        budgets={budgets}
        loading={loading}
        onOpen={openBudget}
        onAdd={addBudget}
        onRename={(id, name) => updateBudget(id, { name })}
        onDelete={deleteBudget}
      />

      <TagsCard
        tags={tags}
        loading={loading}
        onAdd={addTag}
        onUpdate={updateTag}
        onDelete={deleteTag}
      />
    </div>
  );
}

// ----- Budgets -------------------------------------------------------------

function BudgetsCard({
  budgets,
  loading,
  onOpen,
  onAdd,
  onRename,
  onDelete,
}: {
  budgets: Budget[];
  loading: boolean;
  onOpen: (id: string) => void;
  onAdd: (name: string) => Promise<void>;
  onRename: (id: string, name: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      await onAdd(name.trim());
      setName("");
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (id: string) => {
    if (!draft.trim()) return;
    setError("");
    try {
      await onRename(id, draft.trim());
      setEditingId(null);
    } catch (err) {
      setError(message(err));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Wallet className="size-4 text-muted-foreground" /> Budgets
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1">
          {!loading && budgets.length === 0 && (
            <p className="py-2 text-sm text-muted-foreground">No budgets yet. Add your first one below.</p>
          )}

          {budgets.map((b) =>
            editingId === b.id ? (
              <div key={b.id} className="flex items-center gap-2 rounded-lg border p-2">
                <Input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void saveEdit(b.id);
                    if (e.key === "Escape") setEditingId(null);
                  }}
                  autoFocus
                />
                <Button size="icon" variant="ghost" onClick={() => void saveEdit(b.id)} aria-label="Save">
                  <Check className="size-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => setEditingId(null)} aria-label="Cancel">
                  <X className="size-4" />
                </Button>
              </div>
            ) : (
              <div key={b.id} className="group flex items-center gap-2 rounded-lg border p-3">
                <button
                  className="min-w-0 flex-1 truncate text-left font-medium hover:underline"
                  aria-label={`Open ${b.name}`}
                  onClick={() => onOpen(b.id)}
                >
                  {b.name}
                </button>
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Rename ${b.name}`}
                  onClick={() => {
                    setEditingId(b.id);
                    setDraft(b.name);
                  }}
                >
                  <Pencil className="size-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="text-destructive"
                  aria-label={`Delete ${b.name}`}
                  onClick={() => void onDelete(b.id)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            ),
          )}
        </div>

        <div className="flex gap-2">
          <Input
            placeholder="New budget name…"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void submit()}
          />
          <Button aria-label="Add budget" onClick={() => void submit()} disabled={!name.trim() || busy} className="gap-1">
            <Plus className="size-4" /> Add
          </Button>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}

// ----- Expense tags --------------------------------------------------------

function TagsCard({
  tags,
  loading,
  onAdd,
  onUpdate,
  onDelete,
}: {
  tags: ExpenseTag[];
  loading: boolean;
  onAdd: (name: string, color: TagColor) => Promise<void>;
  onUpdate: (id: string, patch: { name?: string; color?: TagColor }) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [color, setColor] = useState<TagColor>(DEFAULT_TAG_COLOR);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [draftColor, setDraftColor] = useState<TagColor>(DEFAULT_TAG_COLOR);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      await onAdd(name.trim(), color);
      setName("");
      setColor(DEFAULT_TAG_COLOR);
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (id: string) => {
    if (!draft.trim()) return;
    setError("");
    try {
      await onUpdate(id, { name: draft.trim(), color: draftColor });
      setEditingId(null);
    } catch (err) {
      setError(message(err));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Tag className="size-4 text-muted-foreground" /> Expense tags
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1">
          {!loading && tags.length === 0 && (
            <p className="py-2 text-sm text-muted-foreground">
              No tags yet. They will be used to categorise expenses.
            </p>
          )}

          {tags.map((t) =>
            editingId === t.id ? (
              <div key={t.id} className="space-y-2 rounded-lg border p-2">
                <div className="flex items-center gap-2">
                  <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void saveEdit(t.id);
                      if (e.key === "Escape") setEditingId(null);
                    }}
                    autoFocus
                  />
                  <Button size="icon" variant="ghost" onClick={() => void saveEdit(t.id)} aria-label="Save">
                    <Check className="size-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => setEditingId(null)} aria-label="Cancel">
                    <X className="size-4" />
                  </Button>
                </div>
                <ColorPicker value={draftColor} onChange={setDraftColor} />
              </div>
            ) : (
              <div key={t.id} className="flex items-center gap-3 rounded-lg border p-3">
                <span className={`size-3 shrink-0 rounded-full ${tagStyle(t.color).dot}`} />
                <span className={cn("truncate rounded px-2 py-0.5 text-sm font-medium", tagStyle(t.color).chip)}>
                  {t.name}
                </span>
                <span className="flex-1" />
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Edit ${t.name}`}
                  onClick={() => {
                    setEditingId(t.id);
                    setDraft(t.name);
                    setDraftColor(t.color);
                  }}
                >
                  <Pencil className="size-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="text-destructive"
                  aria-label={`Delete ${t.name}`}
                  onClick={() => void onDelete(t.id)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            ),
          )}
        </div>

        <div className="space-y-2 rounded-lg border border-dashed p-3">
          <div className="flex gap-2">
            <Input
              placeholder="New tag name…"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void submit()}
            />
            <Button aria-label="Add tag" onClick={() => void submit()} disabled={!name.trim() || busy} className="gap-1">
              <Plus className="size-4" /> Add
            </Button>
          </div>
          <ColorPicker value={color} onChange={setColor} />
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}

function ColorPicker({ value, onChange }: { value: TagColor; onChange: (c: TagColor) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {TAG_COLOR_KEYS.map((key) => (
        <button
          key={key}
          type="button"
          aria-label={key}
          aria-pressed={value === key}
          onClick={() => onChange(key)}
          className={cn(
            "size-6 rounded-full ring-offset-2 ring-offset-background transition-transform",
            TAG_COLORS[key].dot,
            value === key ? "scale-110 ring-2 ring-foreground" : "hover:scale-105",
          )}
        />
      ))}
    </div>
  );
}
